// Bridge between our compact SUBJECTS taxonomy (Layout / wizard checkboxes)
// and the varied subject keys used inside SYLLABUS across different curricula.
//
// Example: our "history" subject maps to SYLLABUS keys "history" (UK KS1-3)
// AND "social" (Common Core / IB PYP) — both contain historical outcomes.
// Without this mapping the wizard's subject filter would silently miss
// aligned content in half the curricula.

/**
 * Subject-ID → list of SYLLABUS subject keys it should surface content for.
 * Kept exhaustive so the same "History" pick works whether the family is
 * on UK KS2, IB PYP, Common Core, or UAE MOE.
 */
export const SUBJECT_TO_SYLLABUS_KEYS = {
  geography: ["geography", "world", "utw", "social"],
  maths:     ["maths"],
  science:   ["science", "world"],
  english:   ["english", "literacy", "ela", "language"],
  history:   ["history", "social"],
  nature:    ["science", "world"],
  space:     ["science"],
  languages: ["languages", "arabic", "language"],
  art:       ["art", "arts", "expressive"],
  ict:       ["computing", "ict", "tech", "design"],
  creative:  ["expressive", "arts", "art"],
  // Optional wellbeing group used by some curricula
  pe:        ["pe", "physical"],
  re:        ["re", "islamic"],
  pshe:      ["pshe"],
};

/**
 * Reverse-lookup: given a SYLLABUS subject key, return our compact ID.
 * Used by the Term Report + Topic-Learn hub when grouping outcomes.
 */
export const SYLLABUS_KEY_TO_SUBJECT = (() => {
  const map = {};
  for (const [sub, keys] of Object.entries(SUBJECT_TO_SYLLABUS_KEYS)) {
    keys.forEach((k) => { if (!map[k]) map[k] = sub; });
  }
  return map;
})();

/** Given a compact subject ID, return every outcome across all curricula. */
export function outcomesForSubject(SYLLABUS, subjectId, age = null) {
  const keys = SUBJECT_TO_SYLLABUS_KEYS[subjectId] || [subjectId];
  const results = [];
  for (const [cur, bySubj] of Object.entries(SYLLABUS)) {
    for (const [k, arr] of Object.entries(bySubj)) {
      if (!keys.includes(k)) continue;
      for (const o of arr) {
        if (age !== null) {
          const [lo, hi] = o.ageBand || [0, 99];
          if (age < lo || age > hi) continue;
        }
        results.push({ ...o, subject: k, curriculum: cur });
      }
    }
  }
  return results;
}
